import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useSession, signOut, startGoogleSignIn, type AppRole } from '../lib/auth'
import { apiJson } from '../lib/api'
import ThemeToggle from './ThemeToggle'
import Mark from './Mark'
import { useCompare } from '../context/CompareContext'

// M3 — Agents / Coaches / Messages nav items are hidden until their directory /
// inbox pages ship (those routes resolve to "Coming soon" stubs today). The nav
// only advertises working destinations. Re-add the entries here when the real
// pages land — nothing else needs to change.
// STRIPE_SPEC Phase 2 — Pricing link added to public tabs (accessible to all).
const browseTabs = [
  { to: '/discover', label: 'Discover' },
  { to: '/browse', label: 'Browse' },
  { to: '/compare', label: 'Compare', badge: true },
  { to: '/pricing', label: 'Pricing' },
]

const appTabs = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/saved', label: 'Saved' },
]

const tabClass = (isActive: boolean) =>
  'px-3 py-1.5 rounded-md transition-colors ' +
  (isActive
    ? 'text-hoop-nav-fg font-semibold'
    : 'text-hoop-nav-fg/60 hover:text-hoop-nav-fg')

// Mobile-drawer link styling — block layout, larger tap target.
const drawerTabClass = (isActive: boolean) =>
  'block px-3 py-2.5 rounded-md text-sm transition-colors ' +
  (isActive
    ? 'bg-hoop-nav-fg/10 text-hoop-nav-fg font-semibold'
    : 'text-hoop-nav-fg/70 hover:bg-hoop-nav-fg/10 hover:text-hoop-nav-fg')

export default function TopNav() {
  const { data: session, isPending: sessionPending } = useSession()
  const user = session?.user ?? null
  const [role, setRole] = useState<AppRole | null>(null)
  const [roleResolved, setRoleResolved] = useState(false)
  const compare = useCompare()
  // M2 — mobile drawer state. Closed on every navigation so a tapped link
  // doesn't leave the menu hanging open.
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const hamburgerRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname, location.search])

  // A4 (a11y) — drawer keyboard + focus handling. On open: move focus into
  // the drawer. Escape and an outside click close it. On close: return focus
  // to the hamburger so keyboard users aren't dropped at the top of the page.
  useEffect(() => {
    if (!mobileOpen) return
    drawerRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node
      if (!drawerRef.current?.contains(t) && !hamburgerRef.current?.contains(t)) {
        setMobileOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    // Defer the outside-click listener a tick so the click that opened the
    // drawer doesn't immediately close it.
    const timer = window.setTimeout(
      () => document.addEventListener('click', onClickOutside),
      0,
    )
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(timer)
      document.removeEventListener('click', onClickOutside)
      hamburgerRef.current?.focus()
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!user) {
      setRole(null)
      setRoleResolved(true)
      return
    }
    setRoleResolved(false)
    let cancelled = false
    apiJson<{ profile: { role: AppRole | null } }>('/api/me')
      .then(({ profile }) => {
        if (cancelled) return
        setRole(profile.role)
        setRoleResolved(true)
      })
      .catch(() => {
        if (cancelled) return
        setRole(null)
        setRoleResolved(true)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  async function handleSignOut() {
    await signOut()
    window.location.href = '/'
  }

  // Sign in straight from the nav — no role hint. This used to be a Link to
  // `/`, which dropped returning users on the sign-up role fork; their role is
  // already set, so the pick was discarded. New users still get the fork by
  // landing on `/` themselves.
  async function handleSignIn() {
    try {
      await startGoogleSignIn()
    } catch {
      // The nav has no error surface. Fall back to `/`, which has both the
      // sign-in control and somewhere to show the failure.
      window.location.href = '/'
    }
  }

  // Logo target: route authed users into the app instead of bouncing them to
  // the sign-in page. `/dashboard` internally dispatches to the agent/coach
  // view. While role is still resolving for an authed user, `/dashboard` is the
  // safe default (Dashboard cascades to /onboarding if no role).
  const logoTarget =
    !user ? '/' : roleResolved && role === null ? '/onboarding' : '/dashboard'

  return (
    <header className="bg-hoop-nav text-hoop-nav-fg border-b border-hoop-nav-fg/10">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4 sm:gap-6">
        {sessionPending ? (
          <span className="flex items-center gap-2 font-display font-semibold tracking-tight text-hoop-nav-fg cursor-default">
            <Mark className="h-7 w-auto" />
            HoopRoster
          </span>
        ) : (
          <Link
            to={logoTarget}
            className="flex items-center gap-2 font-display font-semibold tracking-tight text-hoop-nav-fg"
          >
            <Mark className="h-7 w-auto" />
            HoopRoster
          </Link>
        )}

        {/* Desktop primary nav — hidden below md, where the hamburger takes over. */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          {browseTabs.map(t => (
            <NavLink key={t.label} to={t.to} className={({ isActive }) => tabClass(isActive)}>
              <span className="flex items-center gap-1.5">
                {t.label}
                {t.badge && compare.count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-hoop-accent text-[10px] font-bold text-white">
                    {compare.count}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
          {user && (
            <>
              <span className="mx-2 text-hoop-nav-fg/30" aria-hidden>
                |
              </span>
              {appTabs.map(t => (
                <NavLink key={t.to} to={t.to} className={({ isActive }) => tabClass(isActive)}>
                  {t.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-3 text-sm">
          {/* Desktop-only account controls. */}
          <div className="hidden md:flex items-center gap-3">
            {role && (
              <span className="text-xs uppercase tracking-wider text-hoop-nav-fg/60">
                Viewing as{' '}
                <span className="text-hoop-nav-fg font-medium">{role}</span>
              </span>
            )}
            {/* C6 — hold the auth-dependent controls until the session
                resolves so the nav doesn't flash "Sign in" then flip to
                Settings/Sign out on every load (QA P2). */}
            {sessionPending ? null : user ? (
              <>
                <NavLink to="/settings" className="text-hoop-nav-fg/60 hover:text-hoop-nav-fg">
                  Settings
                </NavLink>
                <button
                  onClick={handleSignOut}
                  className="text-hoop-nav-fg/60 hover:text-hoop-nav-fg"
                  type="button"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={handleSignIn}
                className="text-hoop-nav-fg font-medium"
                type="button"
              >
                Sign in
              </button>
            )}
          </div>

          {/* Theme toggle — single instance, present at every width. */}
          <ThemeToggle />

          {/* Mobile hamburger — below md only. */}
          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-drawer"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-hoop-nav-fg/70 hover:text-hoop-nav-fg hover:bg-hoop-nav-fg/10"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — stacked, full-width tap targets. Hidden at md+. */}
      {mobileOpen && (
        <div
          ref={drawerRef}
          id="mobile-nav-drawer"
          className="md:hidden border-t border-hoop-nav-fg/10 px-4 py-3 space-y-1"
        >
          {browseTabs.map(t => (
            <NavLink key={t.label} to={t.to} className={({ isActive }) => drawerTabClass(isActive)}>
              <span className="flex items-center justify-between">
                {t.label}
                {t.badge && compare.count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-hoop-accent text-[10px] font-bold text-white">
                    {compare.count}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
          {user &&
            appTabs.map(t => (
              <NavLink key={t.to} to={t.to} className={({ isActive }) => drawerTabClass(isActive)}>
                {t.label}
              </NavLink>
            ))}
          <div className="pt-2 mt-2 border-t border-hoop-nav-fg/10 space-y-1">
            {role && (
              <div className="px-3 py-1 text-xs uppercase tracking-wider text-hoop-nav-fg/60">
                Viewing as{' '}
                <span className="text-hoop-nav-fg font-medium">{role}</span>
              </div>
            )}
            {sessionPending ? null : user ? (
              <>
                <NavLink to="/settings" className={({ isActive }) => drawerTabClass(isActive)}>
                  Settings
                </NavLink>
                <button
                  onClick={handleSignOut}
                  type="button"
                  className="block w-full text-left px-3 py-2.5 rounded-md text-sm text-hoop-nav-fg/70 hover:bg-hoop-nav-fg/10 hover:text-hoop-nav-fg"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={handleSignIn}
                type="button"
                className="block w-full text-left px-3 py-2.5 rounded-md text-sm text-hoop-nav-fg font-medium hover:bg-hoop-nav-fg/10"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
